import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ToggleLeft, ToggleRight } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import useSettings from '../hooks/useSettings';
import { updateSettings } from '../api/settings';
import { useAuth } from '../context/AuthContext';
import { isVendor } from '../utils/permissions';
import Members from './Members';
import APIKeys from './APIKeys';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'bn', label: 'বাংলা' },
];

function LanguageCard() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-white">
      <p className="text-sm font-medium text-gray-900">{t('settings.language')}</p>
      <p className="text-sm text-gray-500 mt-1">{t('settings.languageDesc')}</p>
      <div className="flex gap-2 mt-4">
        {LANGUAGES.map(({ code, label }) => (
          <button
            key={code}
            onClick={() => changeLanguage(code)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              i18n.language === code
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SimpleViewCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { simpleView, isLoading } = useSettings();
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (next) => updateSettings({ simpleView: next }).then((r) => r.data.data),
    onSuccess: (data) => { queryClient.setQueryData(['vendor-settings', user?.vendorId], data); setError(''); },
    onError: (err) => setError(err.response?.data?.message || 'Something went wrong.'),
  });

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-900">{t('settings.simpleView')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('settings.simpleViewDesc')}</p>
        </div>
        <button
          onClick={() => mutation.mutate(!simpleView)}
          disabled={isLoading || mutation.isPending}
          aria-pressed={simpleView}
          className={`shrink-0 p-1.5 rounded-lg transition-colors disabled:opacity-60 ${
            simpleView
              ? 'bg-green-50 text-green-600 hover:bg-green-100'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
        >
          {simpleView ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
        </button>
      </div>
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-4">{error}</p>}
    </div>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const vendor = isVendor(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  // Team and API Keys are vendor-only; members just get the General tab.
  const tabs = [
    { key: 'general', label: t('settings.tabGeneral') },
    ...(vendor ? [
      { key: 'team',     label: t('nav.team') },
      { key: 'api-keys', label: t('nav.apiKeys') },
    ] : []),
  ];
  const requested = searchParams.get('tab');
  const tab = tabs.some((x) => x.key === requested) ? requested : 'general';

  // Set here for every tab: this effect runs after the embedded page's own usePageTitle.
  usePageTitle({ general: 'Settings', team: 'Team', 'api-keys': 'API Keys' }[tab]);

  return (
    <div>
      <div className="px-6 pt-6">
        <h1 className="text-xl font-semibold text-gray-900">{t('settings.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('settings.subtitle')}</p>

        {tabs.length > 1 && (
          <div className="flex gap-1 mt-4 border-b border-gray-200">
            {tabs.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setSearchParams(key === 'general' ? {} : { tab: key })}
                className={`px-4 py-2 -mb-px text-sm border-b-2 transition-colors ${
                  tab === key
                    ? 'border-blue-600 text-blue-700 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === 'general' && (
        <div className="p-6 max-w-2xl space-y-4">
          <LanguageCard />
          {vendor && <SimpleViewCard />}
        </div>
      )}
      {tab === 'team' && <Members />}
      {tab === 'api-keys' && <APIKeys />}
    </div>
  );
}
